import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { claimService, getErrorMessage } from '@/services';
import type { ClaimFilters, ClaimCreateRequest } from '@/types';

export function useClaimTypes() {
  return useQuery({
    queryKey: ['claim-types'],
    queryFn: () => claimService.getClaimTypes(),
    staleTime: 60 * 60 * 1000,
  });
}

/** B7: the claim status graph, so the dropdown can offer only the next steps. */
export function useClaimStatusTransitions() {
  return useQuery({
    queryKey: ['claim-status-transitions'],
    queryFn: () => claimService.getStatusTransitions(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useClaims(page: number = 1, filters?: ClaimFilters) {
  return useQuery({
    queryKey: ['claims', page, filters],
    queryFn: () => claimService.getClaims(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useClaim(id: string) {
  return useQuery({
    queryKey: ['claim', id],
    queryFn: () => claimService.getClaim(id),
    enabled: !!id,
  });
}

export function useClaimDocuments(claimId: string) {
  return useQuery({
    queryKey: ['claim-documents', claimId],
    queryFn: () => claimService.getDocuments(claimId),
    enabled: !!claimId,
  });
}

export function useClaimTimeline(claimId: string, enabled = true) {
  return useQuery({
    queryKey: ['claim-timeline', claimId],
    queryFn: () => claimService.getTimeline(claimId),
    enabled: !!claimId && enabled,
  });
}

export function useCreateClaim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClaimCreateRequest) => claimService.createClaim(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      toast.success('Claim created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateClaim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ClaimCreateRequest> }) =>
      claimService.updateClaim(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      queryClient.invalidateQueries({ queryKey: ['claim', variables.id] });
      toast.success('Claim updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeleteClaim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => claimService.deleteClaim(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      toast.success('Claim deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateClaimStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: string; notes?: string }) =>
      claimService.updateStatus(id, status, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      queryClient.invalidateQueries({ queryKey: ['claim', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['claim-timeline', variables.id] });
      toast.success('Claim status updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useAssignSurveyor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ claimId, surveyorId }: { claimId: string; surveyorId: string }) =>
      claimService.assignSurveyor(claimId, surveyorId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      queryClient.invalidateQueries({ queryKey: ['claim', variables.claimId] });
      toast.success('Surveyor assigned successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUploadClaimDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ claimId, formData }: { claimId: string; formData: FormData }) =>
      claimService.uploadDocument(claimId, formData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['claim-documents', variables.claimId] });
      toast.success('Document uploaded successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeleteClaimDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ claimId, documentId }: { claimId: string; documentId: string }) =>
      claimService.deleteDocument(claimId, documentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['claim-documents', variables.claimId] });
      queryClient.invalidateQueries({ queryKey: ['claim', variables.claimId] });
      toast.success('Document deleted');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
